import { Component } from '@theme/component';

/**
 * Mobile peek carousel for collection cards. Desktop stays a grid, so the
 * same blocks are never rendered twice.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} list
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class SkreyCollectionGrid extends Component {
  requiredRefs = ['list'];

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const list = this.refs.list;

    list.addEventListener('scroll', this.#updateControls, { passive: true, signal });
    this.#resizeObserver = new ResizeObserver(this.#updateControls);
    this.#resizeObserver.observe(list);
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    this.#updateControls();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
  }

  /**
   * @param {Event} [event]
   */
  previous(event) {
    event?.preventDefault();
    this.#scrollBy(-1);
  }

  /**
   * @param {Event} [event]
   */
  next(event) {
    event?.preventDefault();
    this.#scrollBy(1);
  }

  /**
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const list = this.refs.list;
    const card = event.target.closest('.collection-card');
    if (!(card instanceof HTMLElement) || list.scrollWidth <= list.clientWidth + 1) return;

    card.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: this.#scrollBehavior });
  };

  #updateControls = () => {
    const list = this.refs.list;
    if (!list) return;

    const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
    const overflowing = maxScroll > 1;
    this.toggleAttribute('data-overflowing', overflowing);

    const previous = this.refs.previous;
    const next = this.refs.next;
    if (!(previous instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

    const position = this.#scrollPosition(list, maxScroll);
    previous.toggleAttribute('disabled', !overflowing || position <= 1);
    next.toggleAttribute('disabled', !overflowing || position >= maxScroll - 1);
  };

  /**
   * @param {number} direction
   */
  #scrollBy(direction) {
    const list = this.refs.list;
    const card = list?.firstElementChild;
    if (!(list instanceof HTMLElement) || !(card instanceof HTMLElement)) return;

    const styles = getComputedStyle(list);
    const gap = Number.parseFloat(styles.columnGap) || 0;
    const distance = card.getBoundingClientRect().width + gap;
    const rtl = styles.direction === 'rtl' ? -1 : 1;

    list.scrollBy({
      left: distance * direction * rtl,
      behavior: this.#scrollBehavior,
    });
  }

  /**
   * Distance from the start of the scroll, in either writing direction.
   * @param {HTMLElement} list
   * @param {number} maxScroll
   */
  #scrollPosition(list, maxScroll) {
    const left = list.scrollLeft;
    if (getComputedStyle(list).direction !== 'rtl') return left;
    if (left < 0) return -left;
    return maxScroll - left;
  }

  get #scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }
}

if (!customElements.get('skrey-collection-grid')) {
  customElements.define('skrey-collection-grid', SkreyCollectionGrid);
}
